import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { LandingCsrfService } from '../services/landing-csrf.service';
import { isSkilledApiRequest } from './skilled-api-request';

/**
 * Sends `x-xsrf-token` on Skilled API calls — required for billing endpoints (same as the
 * main app's AuthInterceptor), and for any write made while the browser holds a signed-in
 * Skilled session cookie.
 *
 * When the API refuses a write for its CSRF token (the token was rotated, or this page
 * could not read the cookie), a fresh token is fetched and the request is sent once more.
 * The refusal comes from middleware ahead of every route, so the first attempt did nothing.
 */
export const landingCsrfInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  if (!isSkilledApiRequest(req.url)) {
    return next(req);
  }

  const csrf = inject(LandingCsrfService);
  const withToken = (token: string) =>
    token ? req.clone({ setHeaders: { 'x-xsrf-token': token } }) : req;

  return next(withToken(csrf.tokenFor(req.url))).pipe(
    catchError((error: unknown) => {
      if (!isCsrfRefusal(error)) return throwError(() => error);
      return csrf
        .refresh(req.url)
        .pipe(switchMap((token) => (token ? next(withToken(token)) : throwError(() => error))));
    }),
  );
};

/** The API's `verifyCsrf` answer: 403 with "Invalid CSRF token". */
export function isCsrfRefusal(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) return false;
  const body = error.error as { message?: unknown; userMessage?: unknown } | string | null;
  const text =
    typeof body === 'string' ? body : `${body?.message ?? ''} ${body?.userMessage ?? ''}`;
  return /csrf/i.test(text);
}
